import { z } from "zod";
import { pageQuery } from "./common";

/**
 * Версии десктоп-приложений (касса Kafe Coco и др.): платформа публикует сборку, касса спрашивает «есть ли новее»
 * и скачивает файл. Сборка — на одно приложение × канал × ОС × архитектуру.
 * draft — загружена, кассам не видна; published — раздаётся; withdrawn — отозвана (кассы откатываются на предыдущую опубликованную).
 */
export const DESKTOP_PLATFORMS = ["windows", "macos", "linux"] as const;
export type DesktopPlatform = (typeof DESKTOP_PLATFORMS)[number];
export const DESKTOP_ARCHS = ["x64", "arm64", "universal"] as const;
export const DESKTOP_CHANNELS = ["stable", "beta"] as const;
export const DESKTOP_RELEASE_STATUSES = ["draft", "published", "withdrawn"] as const;

const SEMVER = /^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)(?:-([0-9A-Za-z-]+(?:\.[0-9A-Za-z-]+)*))?$/;
export const appVersion = z.string().trim().regex(SEMVER, { error: "Версия в формате 1.2.3 или 1.2.3-beta.1" });
const appCode = z.string().trim().regex(/^[a-z][a-z0-9-]{1,31}$/, { error: "Код приложения: латиница, цифры и «-»" });
/** multipart присылает строки: "true"/"false" */
const boolish = z.preprocess((v) => (v === "true" || v === "1" ? true : v === "false" || v === "0" || v === "" ? false : v), z.boolean());
const blank = (v: unknown) => (typeof v === "string" && v.trim() === "" ? undefined : v);

/** Сравнение версий по semver: 1.2.10 > 1.2.9, 1.0.0-beta < 1.0.0. */
export function compareVersions(a: string, b: string): number {
  const pa = SEMVER.exec(a.trim());
  const pb = SEMVER.exec(b.trim());
  if (!pa || !pb) return a.localeCompare(b);
  for (let i = 1; i <= 3; i++) {
    const d = Number(pa[i]) - Number(pb[i]);
    if (d) return Math.sign(d);
  }
  const [ra, rb] = [pa[4], pb[4]];
  if (!ra || !rb) return ra === rb ? 0 : ra ? -1 : 1;
  const xa = ra.split(".");
  const xb = rb.split(".");
  for (let i = 0; i < Math.max(xa.length, xb.length); i++) {
    const [x, y] = [xa[i], xb[i]];
    if (x === undefined) return -1;
    if (y === undefined) return 1;
    const [nx, ny] = [/^\d+$/.test(x), /^\d+$/.test(y)];
    if (nx && ny && Number(x) !== Number(y)) return Math.sign(Number(x) - Number(y));
    if (nx !== ny) return nx ? -1 : 1;
    if (x !== y) return x < y ? -1 : 1;
  }
  return 0;
}

/** Публикация сборки: multipart/form-data, файл — поле `file`, остальные поля — строки. */
export const desktopReleaseCreateSchema = z.strictObject({
  app: appCode.default("pos"),
  version: appVersion,
  channel: z.enum(DESKTOP_CHANNELS).default("stable"),
  platform: z.enum(DESKTOP_PLATFORMS),
  arch: z.enum(DESKTOP_ARCHS).default("x64"),
  notes: z.preprocess(blank, z.string().trim().max(5000).optional()),
  /** Обязательное обновление: касса не даст работать дальше без установки */
  mandatory: boolish.default(false),
  /** Версии ниже этой обязаны обновиться (даже если сама сборка не обязательная) */
  minSupportedVersion: z.preprocess(blank, appVersion.optional()),
  /** Постепенная раскатка: доля касс, которым предлагается версия */
  rolloutPercent: z.coerce.number().int().min(1).max(100).default(100),
  /** SHA-256 файла (hex) — сервер сверит с загруженным */
  sha256: z.preprocess(blank, z.string().trim().toLowerCase().regex(/^[0-9a-f]{64}$/, { error: "SHA-256 — 64 hex-символа" }).optional()),
  /** Сразу опубликовать (иначе — черновик) */
  publish: boolish.default(false),
});
export type DesktopReleaseCreateInput = z.input<typeof desktopReleaseCreateSchema>;

export const desktopReleaseUpdateSchema = z.strictObject({
  notes: z.string().trim().max(5000).nullable().optional(),
  mandatory: z.boolean().optional(),
  minSupportedVersion: appVersion.nullable().optional(),
  rolloutPercent: z.number().int().min(1).max(100).optional(),
});

export const desktopReleasesQuery = pageQuery.extend({
  app: appCode.optional(),
  channel: z.enum(DESKTOP_CHANNELS).optional(),
  platform: z.enum(DESKTOP_PLATFORMS).optional(),
  status: z.enum(DESKTOP_RELEASE_STATUSES).optional(),
});

/** Проверка обновления кассой. Не strict: старые кассы могут присылать лишние параметры. */
export const desktopUpdateQuery = z.object({
  app: appCode.default("pos"),
  platform: z.enum(DESKTOP_PLATFORMS),
  arch: z.enum(DESKTOP_ARCHS).default("x64"),
  channel: z.enum(DESKTOP_CHANNELS).default("stable"),
  /** Установленная версия */
  version: appVersion,
  /** Постоянный id установки (или terminalId) — для раскатки по проценту */
  installId: z.string().trim().max(100).optional(),
});
